import {
  type ComponentType,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Link,
  useParams,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import RRDJoinCampaign from "../rrd/components/RRDJoinCampaign";
import PageLayout from "@/components/layouts/page/PageLayout";
import { campaignService } from "@/features/campaigns/shared/services/campaignService";
import { GlobalContext, LoaderContext } from "@/helpers/contexts/contexts";
import { logger } from "@/helpers/loggerHelper";
import { DIRECT_MAIL_CAMP_PROVIDER } from "../utils/directMailConstants";
import NoDataFound from "@/components/NoDataFound";
import { canJoinCampaign, getDashboardPath } from "@/helpers/utils";
import { Button } from "@mui/material";
import { BRAND_CONST, HTTP_STATUS } from "@/config/constants";
import JoinMailSharkCampaign from "../mail-shark";
import { rrdDirectMailCampaignHelper } from "../rrd/utils/rrdDirectMailHelper";

type CampaignRecord = {
  getCampaignProvider?: string;
  is_brand_paid?: boolean;
  locations?: {
    brandPaid?: unknown[];
    subscribed?: unknown[];
    [key: string]: unknown;
  };
  campaign_advanced_settings?: unknown[];
  campaign_advanced_setting?: unknown;
  [key: string]: unknown;
};

type CampaignResponse = {
  status?: number;
  data?: {
    rows?: CampaignRecord[];
    productList?: unknown;
    [key: string]: unknown;
  } & CampaignRecord;
};

type GlobalContextValue = {
  loggedInUser?: {
    participantData?: {
      id?: string | number;
    };
    [key: string]: unknown;
  };
  selectedBrand?: {
    id?: string | number;
    [key: string]: unknown;
  };
};

type Loader = (_loading: boolean) => void;

type PageLayoutProps = {
  children?: ReactNode;
  className?: string;
};

type JoinMailSharkCampaignProps = {
  campaign: CampaignRecord;
  productList: unknown;
  advanceSettingData: unknown;
};

type RRDJoinCampaignProps = {
  campaign: CampaignRecord;
};

const PageLayoutComponent = PageLayout as ComponentType<PageLayoutProps>;
const JoinMailSharkCampaignComponent =
  JoinMailSharkCampaign as ComponentType<JoinMailSharkCampaignProps>;
const RRDJoinCampaignComponent =
  RRDJoinCampaign as ComponentType<RRDJoinCampaignProps>;

function DirectMailJoinCampaignPage() {
  const navigate = useNavigate();
  const { campaignId } = useParams();
  const [searchParams] = useSearchParams();

  const [campaign, setCampaign] = useState<CampaignRecord | null>(null);
  const [productList, setProductList] = useState<unknown>(null);
  const [advanceSettingData, setAdvancedSettingData] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);

  const { loggedInUser, selectedBrand } = useContext(
    GlobalContext
  ) as GlobalContextValue;
  const loader = useContext(LoaderContext) as Loader;

  const participantId =
    searchParams.get("participantId") || loggedInUser?.participantData?.id;

  useEffect(() => {
    if (!campaignId || !selectedBrand?.id || !participantId) return;

    async function fetchCampaign() {
      setLoading(true);
      try {
        const campaignReqData = {
          id: campaignId,
          brand_id: selectedBrand?.id,
          fk_participant_id: participantId,
        };
        const res = (await campaignService.getPostcardCampaign(
          campaignReqData
        )) as CampaignResponse;

        if (res.status === HTTP_STATUS.OK) {
          if (res.data?.rows?.[0]) {
            let campaignData = res.data?.rows[0];
            const getProductList = res.data?.productList;

            let campaignAdvancedSettings =
              campaignData && campaignData?.campaign_advanced_settings?.[0];

            if (!campaignData) {
              setCampaign(null);
              return;
            } else {
              if (campaignData.is_brand_paid) {
                campaignData.locations!.subscribed =
                  campaignData?.locations?.brandPaid &&
                  campaignData.locations.brandPaid.length
                    ? campaignData.locations.brandPaid
                    : campaignData.locations.subscribed;
              }

              if (campaignAdvancedSettings) {
                campaignData.campaign_advanced_setting =
                  campaignAdvancedSettings;
              }

              setCampaign(campaignData);
            }

            if (getProductList) {
              setProductList(getProductList);
            }
            if (campaignAdvancedSettings) {
              setAdvancedSettingData(campaignAdvancedSettings);
            }
          } else {
            const campaignData = res.data;
            setCampaign(campaignData);
          }
        }
      } catch (err) {
        logger.error("fetchCampaign error", err);
      } finally {
        setLoading(false);
      }
    }
    fetchCampaign();
  }, [campaignId, selectedBrand?.id, participantId]);

  useEffect(() => {
    loader(loading);
  }, [loading, loader]);

  const campaignProvider = useMemo(() => {
    if (!campaign) return null;
    return campaign.getCampaignProvider;
  }, [campaign]);

  const renderRRDCampaign = useCallback(() => {
    if (!rrdDirectMailCampaignHelper.canJoinRRDCampaign(campaign)) {
      return (
        <NoDataFound
          message={
            <div className="d-flex-col-center">
              <div>
                You have already joined this campaign or it is
                in-active/completed/not customizable.
              </div>
              <Button
                component={Link}
                to={`${getDashboardPath()}`}
                variant="contained"
                color="primary"
                className="my-4"
                size="large"
              >
                Back to dashboard
              </Button>
            </div>
          }
        />
      );
    }

    return <RRDJoinCampaignComponent campaign={campaign} />;
  }, [campaign]);

  const renderMailSharkCampaign = useCallback(() => {
    if (!canJoinCampaign(campaign)) {
      return (
        <NoDataFound
          message={
            <div className="d-flex-col-center">
              <div>
                You have already joined this campaign or it is
                in-active/completed/not customizable.
              </div>
              <Button
                component={Link}
                to={`${getDashboardPath()}`}
                variant="contained"
                color="primary"
                className="my-4"
                size="large"
              >
                Back to dashboard
              </Button>
            </div>
          }
        />
      );
    }

    return (
      <JoinMailSharkCampaignComponent
        campaign={campaign}
        productList={productList}
        advanceSettingData={advanceSettingData}
      />
    );
  }, [campaign, productList, advanceSettingData]);

  if (
    selectedBrand?.id === BRAND_CONST.MS_BRAND_ID ||
    selectedBrand?.id === BRAND_CONST.KUMON_BRAND_ID
  ) {
    // This feature is not available for this brand MS and Kumon
    navigate(`${getDashboardPath()}`, {
      replace: true,
    });
    return null;
  }

  return (
    <PageLayoutComponent className="join-campaign-page postcard-container">
      {loading ? (
        <NoDataFound message="Loading..." />
      ) : !campaign ? (
        <NoDataFound message={`No campaign found!`} />
      ) : DIRECT_MAIL_CAMP_PROVIDER.RRD === campaignProvider ? (
        renderRRDCampaign()
      ) : (
        renderMailSharkCampaign()
      )}
    </PageLayoutComponent>
  );
}

export default DirectMailJoinCampaignPage;
